import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  RefreshCw,
  Undo2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { SparkleIcon } from '@/components/ui/sparkle-icon'
import { cn } from '@/lib/utils'
import type { NarrativeCardDraft, NarrativeCardStatus } from '@digital-twins/shared-types'

interface NarrativeCardEditorProps {
  draft: NarrativeCardDraft | null
  status: NarrativeCardStatus | null
  onUpdate: (draft: NarrativeCardDraft) => void
  onRetry: () => void
  /** Called when no card exists yet and the user wants to generate one. */
  onGenerate?: () => void
}

const editableField = 'border border-border rounded-[6px] p-2 text-base text-foreground bg-transparent w-full focus:outline-none focus:ring-1 focus:ring-ring transition-colors placeholder:text-muted-foreground'

function InlineEditableText({
  value,
  onChange,
  label,
  originalValue,
  multiline,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  label: string
  originalValue?: string
  multiline?: boolean
  placeholder?: string
}) {
  const hasOriginal = originalValue !== undefined && !!originalValue
  const isModified = hasOriginal && value !== originalValue
  const isAiSuggested = hasOriginal && value === originalValue

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Label className="text-sm font-normal text-muted-foreground">{label}</Label>
          {isAiSuggested && (
            <SparkleIcon className="h-3 w-3 text-muted-foreground/50" />
          )}
        </div>
        {isModified && (
          <button
            type="button"
            onClick={() => onChange(originalValue)}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground/70 transition-colors hover:text-foreground"
          >
            <Undo2 className="h-3 w-3" />
            Restore
          </button>
        )}
      </div>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          placeholder={placeholder}
          className={cn(editableField, 'resize-none')}
          style={{ outline: 'none', boxShadow: 'none' }}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={cn(editableField, 'h-[38px]')}
          style={{ outline: 'none', boxShadow: 'none' }}
        />
      )}
    </div>
  )
}

function profileToText(items: Array<{ field: string; value: string }>): string {
  return items.map(item =>
    item.value ? `${item.field} — ${item.value}` : item.field,
  ).join('\n')
}

function textToProfile(text: string): Array<{ field: string; value: string }> {
  return text.split('\n')
    .filter(line => line.trim())
    .map(line => {
      const clean = line.replace(/^[•\-*]\s*/, '').trim()
      const idx = clean.indexOf(' — ')
      if (idx >= 0) return { field: clean.slice(0, idx), value: clean.slice(idx + 3) }
      const idx2 = clean.indexOf('—')
      if (idx2 >= 0) return { field: clean.slice(0, idx2).trim(), value: clean.slice(idx2 + 1).trim() }
      return { field: clean, value: '' }
    })
}

function insightsToText(items: Array<{ title: string; description: string }>): string {
  return items.map(item =>
    item.description ? `${item.title} — ${item.description}` : item.title,
  ).join('\n')
}

function textToInsights(text: string): Array<{ title: string; description: string }> {
  return text.split('\n')
    .filter(line => line.trim())
    .map(line => {
      const clean = line.replace(/^[•\-*]\s*/, '').trim()
      const idx = clean.indexOf(' — ')
      if (idx >= 0) return { title: clean.slice(0, idx), description: clean.slice(idx + 3) }
      const idx2 = clean.indexOf('—')
      if (idx2 >= 0) return { title: clean.slice(0, idx2).trim(), description: clean.slice(idx2 + 1).trim() }
      return { title: clean, description: '' }
    })
}

export function NarrativeCardEditor({
  draft,
  status,
  onUpdate,
  onRetry,
  onGenerate,
}: NarrativeCardEditorProps) {
  const { t } = useTranslation('app')
  const [profileText, setProfileText] = useState<string | null>(null)
  const [insightsText, setInsightsText] = useState<string | null>(null)
  const initialDraftRef = useRef<NarrativeCardDraft | null>(null)

  if (draft && !initialDraftRef.current) {
    initialDraftRef.current = { ...draft }
  }
  const initialDraft = initialDraftRef.current

  if (status === 'pending' || status === 'generating') {
    return (
      <div className="flex flex-col gap-4 rounded-2xl border border-border/40 bg-muted/10 p-6">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 animate-pulse rounded-full bg-primary-dark" />
          <span className="text-sm text-muted-foreground">
            Generating narrative card…
          </span>
        </div>
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <div className="flex gap-4">
          <Skeleton className="h-16 flex-1" />
          <Skeleton className="h-16 flex-1" />
        </div>
      </div>
    )
  }

  if (status === 'failed') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-destructive/20 bg-destructive/5 p-6">
        <AlertCircle className="h-5 w-5 text-destructive/70" />
        <p className="text-sm text-muted-foreground">
          Narrative card generation failed.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={onRetry}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    )
  }

  if (!draft) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-border/40 bg-muted/10 p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Generate an AI narrative with Audience profile and Key insights for this audience.
        </p>
        {onGenerate && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={onGenerate}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Generate
          </Button>
        )}
      </div>
    )
  }

  const updateField = <K extends keyof NarrativeCardDraft>(
    key: K,
    value: NarrativeCardDraft[K],
  ) => {
    onUpdate({ ...draft, [key]: value })
  }

  const initialProfileTextValue = initialDraft
    ? profileToText(initialDraft.audienceProfile)
    : undefined
  const initialInsightsTextValue = initialDraft
    ? insightsToText(initialDraft.topLineInsights)
    : undefined

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-4">
        <InlineEditableText
          label={t('audienceCreate.narrativeHeader')}
          value={draft.header}
          originalValue={initialDraft?.header}
          onChange={(v) => updateField('header', v)}
        />
        <InlineEditableText
          label={t('audienceCreate.narrativeSubheader')}
          value={draft.subheader}
          originalValue={initialDraft?.subheader}
          onChange={(v) => updateField('subheader', v)}
        />
      </div>

      <div className="border-t border-border/40" />

      <div className="flex flex-col gap-4">
        <InlineEditableText
          label={t('audienceCreate.narrativeBody1')}
          value={draft.bodyParagraph1}
          originalValue={initialDraft?.bodyParagraph1}
          onChange={(v) => updateField('bodyParagraph1', v)}
          multiline
        />
        <InlineEditableText
          label={t('audienceCreate.narrativeBody2')}
          value={draft.bodyParagraph2 ?? ''}
          originalValue={initialDraft?.bodyParagraph2 ?? undefined}
          onChange={(v) => updateField('bodyParagraph2', v || null)}
          multiline
          placeholder={t('audienceCreate.narrativeBody2Placeholder')}
        />
      </div>

      <div className="border-t border-border/40" />

      <div className="flex gap-4">
        <div className="flex-1 min-w-0">
          <InlineEditableText
            label={t('audienceCreate.narrativeProfile')}
            value={profileText ?? profileToText(draft.audienceProfile)}
            originalValue={initialProfileTextValue}
            onChange={(v) => {
              setProfileText(v)
              updateField('audienceProfile', textToProfile(v))
            }}
            multiline
            placeholder={t('audienceCreate.narrativeProfilePlaceholder')}
          />
        </div>
        <div className="flex-1 min-w-0">
          <InlineEditableText
            label={t('audienceCreate.narrativeInsights')}
            value={insightsText ?? insightsToText(draft.topLineInsights)}
            originalValue={initialInsightsTextValue}
            onChange={(v) => {
              setInsightsText(v)
              updateField('topLineInsights', textToInsights(v))
            }}
            multiline
            placeholder={t('audienceCreate.narrativeInsightsPlaceholder')}
          />
        </div>
      </div>

    </div>
  )
}
